import * as React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { Tooltip } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import {
  getActivitiesList,
  getActivitiesStatusList,
  getCustomActivities,
} from '../../api/activities';

import {
  createPermitInspection,
  deletePermitInspection,
  updatePermitInspection,
} from '../../api/inspections';
import { getInspectionTypes, downloadInspectionReport } from '../../api/inspectionTypes';
import {
  DEFAULT_DATE_FORMAT,
  GET_ALL_QUERY_PARAMS,
} from '../../constants';

import PermitInspectionsForm from '../../forms/permitInspectionForm/PermitInspectionsForm';

import useTablePage from '../../hooks/useTablePage';

import { ModalDefaultDraggable } from '../../molecules';
import ModalInfo from '../../molecules/ModalInfo';
import ContactNotAvailableModal from '../../molecules/ContactNotAvailableModal';

import Table from '../../organisms/Table';

import { convertTableBoolean } from '../../utils/convertTableValues';
import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import { USER_TYPES_VALUES } from '../../constants/forms';
import { formatDateTime } from '../../utils/formatDate';
import InspectionsInfoBlock from '../../organisms/InspectionsInfoBlock';
import { getPermitByID } from '../../api/permits';
import useUserRole from '../../hooks/useUserRole';
import InspectionDeletePopup from './InspectionDeletePopup';
import { setDeleteError, setShowDeleteMessage, setShowDeleteWarning } from '../../redux/slices/inspectionsSlice';
import { RootState } from '../../redux/root';
import InspectionCardList from './InspectionCardList';
import BulkScheduleInspectionsModal, { BulkScheduleRow } from './BulkScheduleInspectionsModal';

const InspectionsPage = ({
  projectId,
  permit,
  permitStatus,
  isAddInspection,
  setAddInspection,
  isReadOnly,
  cameFromSearch,
  onCountChange,
  onPermitStatusChange,
}: Props) => {
  const {
    tableManuallyUpdate,
    updateTable,
    search,
    handleEdit,
    isModalOpen,
    createOrUpdate,
    modalSuccess,
    modalError,
    modalTitle,
    modalErrorTitle,
    errors,
    values,
    handleCloseModalInfo,
    handleCloseModal,
    handleAdd,
    setValues,
  } = useTablePage({
    updateEndpoint: updatePermitInspection,
    createEndpoint: createPermitInspection,
    modalCreateTitle: 'Add Inspection',
    modalUpdateTitle: 'Update Inspection',
  });

  const [selectedRecord, setSelectedRecord] = useState<any>();
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showClearWarning, setShowClearWarning] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState<boolean>(false);
  // Bumped after a bulk action to remount the Table and clear its internal
  // (react-table) row selection.
  const [tableKey, setTableKey] = useState<number>(0);
  const dispatch = useDispatch();
  const {
    isEmployee,
    isInspector,
  } = useUserRole();

  // Bulk scheduling is only meaningful once the permit is issued — until then
  // every row's selection checkbox is greyed out and the toolbar shows a
  // warning instead of the usual "select inspections" hint.
  const permitIssued = useSelector(
    (s: RootState) => !!s.projectsSlice.selectedPermit?.permitIssued,
  );

  const [inspectionCount, setInspectionCount] = React.useState(0);

  const [isLoading, setIsLoading] = React.useState<boolean>(
    true,
  );
  const [additionalRowData, setAdditionalRowData] = React.useState(null);

  const [readOnlyInfo, setReadOnlyInfo] = React.useState<any>(
    [],
  );

  const [inspectionDetails, setInspectionDetails] = React.useState<any>(
    {},
  );

  React.useEffect(() => {
    if (isAddInspection) {
      handleAdd();
    }
  }, [isAddInspection]);

  React.useEffect(() => {
    updateTable();
    if (modalSuccess && !modalError) {
      setAddInspection(false);
      // Scheduling can move the permit to Inspection Started server-side.
      if (onPermitStatusChange) onPermitStatusChange();
    }
  }, [modalSuccess, modalError]);

  const closeInfoModal = () => {
    updateTable();
    handleCloseModalInfo();
  };

  const columns = column => {
    if (
      [
        'permit',
        'info',
        'information',
        'trades',
        'isEmergency',
        'period',
        'isCompleted',
        'report',
        'isDeleted',
        'isViewOnly',
        'lastComment',
        'permitName',
        'permitNumber',
        'projectName',
        'projectJurisdiction',
        'projectAddress',
        'contactFirstName',
        'contactLastName',
        'companyName',
        'assignedToFirstName',
        'assignedToLastName',
        'inspectorFirstName',
        'inspectorLastName',
        'activityTypeName',
        'activityTitleName',
        'statusName',
        'projectId',
        'managerFirstName',
        'managerLastName',
        'phoneNumber',
        'cellPhone',
        'contactId',
        'type',
        'createdAt',
        'createdBy',
        'completedAt',
        'completedBy',
        'visibleFileName',
        'assignedToId',
        'isRequestedAtf',
        'titleId',
        'contactEmail',
        'contactIsActive',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'title':
        return 'Activity Title';
      case 'status':
        return 'Activity Status';
      case 'id':
        return 'Inspection Number';
      case 'inspectionType':
        return 'Inspection Type';
      case 'inspectionDate':
        return 'Inspection Date';
      case 'inspector':
        return 'Inspector';
      case 'contact':
        return 'Activity Contact';
      case 'followUp':
        return 'Follow Up Date';
      case 'assignedTo':
        return 'Assigned To';
      case 'visibleFilename':
        return 'Inspection Report';
      case 'createdAt':
        return 'Date Created';
      case 'completedAt':
        return 'Date Completed';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const columnsOrder = [
    'title',
    'status',
    'id',
    'inspectionType',
    'inspectionDate',
    'inspector',
    'contact',
    'followUp',
    'assignedTo',
    'createdAt',
    'createdBy',
    'completedAt',
    'completedBy',
  ];

  // EA-1006: make the "Inspection Report" (visibleFilename) cell a clickable
  // link that downloads the report directly, instead of opening the edit modal.
  const reportCellClassNames = [{ column: 'visibleFilename', className: 'link-cell' }];
  const reportCellCallbackFnIsOn = ['visibleFilename'];

  const handleReportClick = (rowValue: any) => {
    if (!rowValue?.visibleFilename || !rowValue?.id) return;
    downloadInspectionReport(rowValue.id)
      .then(file => {
        const dataUrl = `data:application/pdf;application/docx;base64,${file.data.file}`;
        const arr = dataUrl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const myFile = new File([u8arr], file.data.fileName, { type: mime });
        const url = window.URL.createObjectURL(myFile);
        const a = document.createElement('a');
        a.href = url;
        a.download = rowValue.visibleFilename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => console.log(err));
  };

  // Inspector / inspection date / follow-up date are wiped as part of the delete, so a
  // row still carrying any of them gets an extra confirmation about that clearing
  // before the usual "are you sure you want to delete" step. A row with none of them
  // goes straight to the single delete confirmation.
  const hasScheduleInfo = (record: any) =>
    Boolean(record?.inspector || record?.inspectionDate || record?.followUp);

  const onDelete = arg => {
    setSelectedRecord(arg);
    const error = arg.statusName !== "Not Scheduled";
    if (error) {
      dispatch(setDeleteError(error));
      dispatch(setShowDeleteMessage(true));
      return;
    }
    if (hasScheduleInfo(arg)) {
      setShowClearWarning(true);
      return;
    }
    dispatch(setShowDeleteWarning(true));
  };

  const handleConfirmClear = () => {
    setShowClearWarning(false);
    dispatch(setShowDeleteWarning(true));
  };

  const handleCancelClear = () => setShowClearWarning(false);

  const handleDeleteInspection = async () => {
    const result = await deletePermitInspection({ id: selectedRecord.id });
    if (result?.data?.success) {
      setShowSuccess(true);
      dispatch(setShowDeleteWarning(false));
      updateTable();
    }
  };

  const handleBulkSelect = React.useCallback((rows: any[]) => {
    setSelectedRows(rows || []);
  }, []);

  const bulkRows: BulkScheduleRow[] = selectedRows.map(row => ({
    id: row.id,
    titleId: row.titleId,
    // EA-1123 parity: carry trades (and the raw type id as fallback) so the
    // Inspector dropdown in the bulk modal can filter to qualified inspectors.
    trades: Array.isArray(row.trades) ? row.trades : undefined,
    inspectionTypeId:
      typeof row.inspectionType === 'number'
        ? row.inspectionType
        : (row.inspectionTypeId ?? row.activityType ?? null),
  }));

  const handleBulkSuccess = () => {
    setIsBulkModalOpen(false);
    setSelectedRows([]);
    setTableKey(prev => prev + 1);
    setShowSuccess(true);
    updateTable();
    if (onPermitStatusChange) onPermitStatusChange();
  };
  const modifiedGetPermitInspections = params =>
    Promise.all([
      getCustomActivities({
        ...params,
        // permit,
        permit_id: permit,
        sort_by: params?.ordering,
        ordering: null,
        event_type: "Inspection",
      }),
    ])
      .then(
        ([
          inspectionsResponse,
        ]) => {
          setInspectionCount(inspectionsResponse?.data?.highestCount);
          setIsLoading(false);
          // Permit-level header fields (project/permit name, address, number)
          // are identical on every inspection row, so reuse the first row here
          // instead of a separate request to the heavy custom-activities endpoint.
          if (inspectionsResponse?.data?.results?.[0]) {
            setInspectionDetails(inspectionsResponse.data.results[0]);
          }
          const informationData = inspectionsResponse.data.results.map(
            el => ({
              id: el.id,
              information: el.information,
            }),
          );
          setAdditionalRowData({
            commentData: [],
            informationData,
            isDailyInspection: true,
          });
            return {
            ...inspectionsResponse,
            data: {
              ...inspectionsResponse.data,
              results: inspectionsResponse.data.results.map(
                inspection => {
                    return {
                      ...inspection,
                      title: inspection?.activityTitleName,
                      // Raw activity-title id, preserved for bulk scheduling
                      // (the `title` field above is overwritten with the name).
                      titleId: inspection?.title,
                      status: inspection?.statusName,
                      id: inspection?.id,
                      inspectionType: inspection?.activityTypeName,
                      inspector: inspection?.inspectorFirstName
                        ? `${inspection?.inspectorFirstName} ${inspection?.inspectorLastName}`
                        : '',
                      contact: `${inspection?.contactFirstName || ""} ${
                        inspection?.contactLastName || ""
                      }${
                        inspection?.companyName ||
                        inspection?.parentCompany || ""
                          ? ` - ${
                              inspection?.companyName ||
                              inspection?.parentCompany || ""
                            }`
                          : ''
                      }`,
                      assignedToId: inspection?.assignedTo,
                      assignedTo: `${inspection?.assignedToFirstName || ""} ${inspection?.assignedToLastName || ""}`,
                      inspectionDate: inspection?.inspectionDate
                        ? formatDateTime(
                            inspection.inspectionDate,
                            DEFAULT_DATE_FORMAT,
                          )
                        : '',
                      followUp:
                        inspection?.followUp
                        // inspection?.followUp !== '01/01/2050'
                          ? formatDateTime(
                              inspection.followUp,
                              DEFAULT_DATE_FORMAT,
                            )
                          : '',
                      createdAt:
                      inspection?.createdAt
                        ? formatDateTime(
                            inspection.createdAt,
                            DEFAULT_DATE_FORMAT,
                          )
                        : '',
                        isDeleted: cameFromSearch,
                        // Completed inspections are view-only: the Table swaps
                        // Edit/Delete for the View eye. Un-completing one in the
                        // backend brings the buttons back.
                        isViewOnly: !!inspection?.isCompleted,
                    };
                },
              ),
            },
          };
        },
      )
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => {});

  const isViewingCompleted = Boolean(values?.id && values?.isCompleted);

  const handleCloseInspectionModal = () => {
    if (!modalError && !modalSuccess) {
      setAddInspection(false);
      handleCloseModal();
    }
  };

  React.useEffect(() => {
    setReadOnlyInfo([
      {
        label: 'Project Name',
        value: inspectionDetails?.projectName,
      },
      {
        label: 'Permit Name',
        value: inspectionDetails?.permitName,
      },
      {
        label: 'Permit Address',
        value: inspectionDetails?.projectAddress,
      },
      {
        label: 'Permit Number',
        value: inspectionDetails?.permitNumber,
      },
    ]);
  }, [inspectionDetails]);

  if (isReadOnly) {
    return (
      <ReadOnlyRoot>
        {inspectionDetails?.id && <InspectionsInfoBlock info={readOnlyInfo} />}
        <InspectionCardList
          permit={permit}
          onCountChange={onCountChange}
          onDetailsLoad={setInspectionDetails}
        />
      </ReadOnlyRoot>
    );
  }

  return (
    <>
      {showSuccess && <ModalInfo
        title="Success"
        timeToClose={5000}
        isError={false}
        onClose={() => setShowSuccess(false)}
        errorMsg=""
      />}
      {!isLoading && (
      <InpectionDetailsBlock>
        {isReadOnly ?
          <InspectionsInfoBlock info={readOnlyInfo} /> : ''}
        <h3>
          Total Inspections:{' '}
          <StyledNumber>
            {inspectionCount}
          </StyledNumber>
        </h3>
      </InpectionDetailsBlock>
      )}
      <InspectionDeletePopup
        selectedRecord={selectedRecord}
        handleDeleteInspection={handleDeleteInspection}
        showClearWarning={showClearWarning}
        onConfirmClear={handleConfirmClear}
        onCancelClear={handleCancelClear}
      />
      <Table
        key={tableKey}
        customSearch
        initialUseSortBy
        permitInspectionTable
        initialPageSize={100}
        searchQuery={search}
        columns={columns}
        columnsOrder={columnsOrder}
        colorSchema
        rows={modifiedGetPermitInspections}
        onEdit={isReadOnly ? null : handleEdit}
        onDelete={onDelete}
        onSelect={isReadOnly ? null : handleBulkSelect}
        selectableRow={(row: any) =>
          permitIssued && row?.status === 'Not Scheduled'
        }
        onConfigure={null}
        formatValue={convertTableBoolean}
        manuallyUpdate={tableManuallyUpdate}
        additionalRow={isInspector ? additionalRowData : null}
        handleCellClick={handleReportClick}
        cellCallbackFnIsOn={reportCellCallbackFnIsOn}
        cellClassName={reportCellClassNames}
        italic
        isPermitActivitiesPage
      />
      {!isReadOnly && (
        <BulkActionBar>
          {!permitIssued ? (
            <WarningMessage>
              <WarningRoundedIcon fontSize="inherit" />
              Bulk scheduling is unavailable while the permit is Not Issued.
              Issue the permit to schedule inspections together.
            </WarningMessage>
          ) : (
            <SelectionCount>
              {selectedRows.length > 0
                ? `${selectedRows.length} selected`
                : 'Select inspections to schedule them together'}
              <Tooltip
                arrow
                placement="top"
                title="Only Not Scheduled inspections can be selected. If you want to select other inspections, they need to be changed to Not Scheduled status first."
              >
                <InfoIcon>
                  <InfoOutlinedIcon fontSize="inherit" />
                </InfoIcon>
              </Tooltip>
            </SelectionCount>
          )}
          <BulkScheduleButton
            type="button"
            disabled={!permitIssued || selectedRows.length === 0}
            onClick={() => setIsBulkModalOpen(true)}
          >
            Bulk Schedule
          </BulkScheduleButton>
        </BulkActionBar>
      )}
      {isBulkModalOpen && (
        <ModalDefaultDraggable
          title="Bulk Schedule Inspections"
          onClose={() => setIsBulkModalOpen(false)}
        >
          <BulkScheduleInspectionsModal
            selectedInspections={bulkRows}
            permitId={permit}
            onClose={() => setIsBulkModalOpen(false)}
            onSuccess={handleBulkSuccess}
          />
        </ModalDefaultDraggable>
      )}
      {isModalOpen && (
      <ModalDefaultDraggable
        title={isViewingCompleted ? 'View Inspection' : modalTitle}
        onClose={handleCloseInspectionModal}
      >
        <PermitInspectionsForm
          projectId={projectId}
          onSubmit={createOrUpdate}
          initialValues={values}
          modalError={modalError}
          addButtonText="Create"
          updateButtonText="Update"
          initialErrors={errors}
          permit={permit}
          permitStatus={permitStatus}
          isReadOnly={isViewingCompleted}
          setInitialValues={setValues}
          updateTable={updateTable}
        />
      </ModalDefaultDraggable>
        )}
      {isModalOpen && (
        <ContactNotAvailableModal key={values?.id} record={values} />
      )}
      {(modalSuccess || modalError) && (
      <ModalInfo
        timeToClose={5000}
        isError={modalError}
        onClose={closeInfoModal}
        errorMsg={modalErrorTitle}
      />
        )}
    </>
  );
};

interface Props {
  projectId?: number | string;
  permit: number;
  permitStatus?: any;
  isAddInspection: boolean;
  setAddInspection: (data: boolean) => void;
  isReadOnly: boolean;
  cameFromSearch?: boolean;
  onCountChange?: (count: number) => void;
  onPermitStatusChange?: () => void;
}
const StyledNumber = styled.span`
  color: var(--primary);
`;

// Sticky bottom toolbar — pinned to the bottom of the scroll viewport so the
// Bulk Schedule action stays visible no matter how far the inspections table is
// scrolled. Rendered AFTER the table so sticky `bottom` keeps it within the
// content box while that box extends below the viewport.
const BulkActionBar = styled.div`
  position: sticky;
  bottom: 12px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-top: 12px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
`;

const SelectionCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

const WarningMessage = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--warning-700);

  svg {
    font-size: 20px;
    color: var(--warning-500);
    flex-shrink: 0;
  }
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--neutral-400);
  font-size: 18px;
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const BulkScheduleButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const InpectionDetailsBlock = styled.div`
`;

const ReadOnlyRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
export default InspectionsPage;
